import React from "react";
import "./EngineeringCulture.css";

type CultureIconProps = {
  type: "collaboration" | "learning" | "execution" | "success";
};

const CultureIcon: React.FC<CultureIconProps> = ({ type }) => {
  if (type === "collaboration") {
    return (
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <circle cx="32" cy="14" r="7" />
        <circle cx="14" cy="46" r="7" />
        <circle cx="50" cy="46" r="7" />
        <path d="M28 20 18 39M36 20l10 19M22 46h20" />
        <path d="M25 15c0-4 3-7 7-7s7 3 7 7M7 47c0-4 3-7 7-7s7 3 7 7M43 47c0-4 3-7 7-7s7 3 7 7" />
      </svg>
    );
  }

  if (type === "learning") {
    return (
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <path d="M8 20c10-2 18 1 24 7v27c-6-6-14-9-24-7V20ZM56 20c-10-2-18 1-24 7v27c6-6 14-9 24-7V20Z" />
        <path d="m32 7 2 6 6 2-6 2-2 6-2-6-6-2 6-2 2-6Z" />
      </svg>
    );
  }

  if (type === "execution") {
    return (
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <rect x="8" y="8" width="40" height="48" rx="5" />
        <path d="m16 21 3 3 5-6M16 34l3 3 5-6M16 47l3 3 5-6M29 21h10M29 34h7" />
        <circle cx="47" cy="45" r="11" />
        <path d="M47 38v14M40 45h14" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 64 64" aria-hidden="true">
      <circle cx="18" cy="25" r="7" />
      <circle cx="34" cy="20" r="8" />
      <circle cx="50" cy="25" r="7" />
      <path d="M6 50v-6c0-7 5-12 12-12 3 0 6 1 8 3M58 50v-6c0-7-5-12-12-12-3 0-6 1-8 3M20 54v-8c0-8 6-14 14-14s14 6 14 14v8" />
      <path d="M47 19 56 10M49 10h7v7" />
    </svg>
  );
};

const principles = [
  {
    icon: "collaboration" as const,
    title: "Collaborative Problem Solving",
    description:
      "Work alongside multidisciplinary teams to solve complex semiconductor engineering challenges.",
  },
  {
    icon: "learning" as const,
    title: "Continuous Learning",
    description:
      "Expand your technical expertise through new technologies, engineering practices, and real-world project experience.",
  },
  {
    icon: "execution" as const,
    title: "Disciplined Execution",
    description:
      "Contribute to engineering programs that value quality, consistency, and structured execution.",
  },
  {
    icon: "success" as const,
    title: "Shared Success",
    description:
      "Grow in an environment where knowledge is shared, achievements are celebrated, and success is built together.",
  },
];

const EngineeringCulture: React.FC = () => (
  <section className="engineering-culture" aria-labelledby="culture-title">
    <div className="engineering-culture__circuit" aria-hidden="true" />
    <div className="container">
      <header className="engineering-culture__header">
        <p className="engineering-culture__eyebrow">
          <span /> Engineering Culture <span />
        </p>
        <h2 id="culture-title" className="engineering-culture__title">
          An Engineering Culture Built on
          <span>Collaboration and Excellence</span>
        </h2>
        <p className="engineering-culture__intro">
          Strong engineering is built on more than technical expertise. It
          thrives in an environment where collaboration, continuous learning,
          disciplined execution, and mutual respect come together to solve
          complex challenges.
        </p>
        <p className="engineering-culture__supporting">
          At Digicomm Semiconductor, our teams work across engineering
          disciplines, sharing knowledge, embracing new technologies, and
          supporting one another throughout the semiconductor development
          lifecycle.
        </p>
      </header>

      <div className="engineering-culture__grid">
        {principles.map((principle, index) => (
          <article className="culture-card" key={principle.title}>
            <span className="culture-card__number">
              {String(index + 1).padStart(2, "0")}
            </span>
            <div className="culture-card__accent" />
            <div className="culture-card__icon">
              <CultureIcon type={principle.icon} />
            </div>
            <h3>{principle.title}</h3>
            <p>{principle.description}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
);

export default EngineeringCulture;
